import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../services/api';
import { fmtDate } from './format';
import { c, t, tbl } from '../../theme';
import { useSort, sortRows, SortTh } from '../Shared/sortable';
import ExpandableScroll from '../Shared/ExpandableScroll';
import { useRowLimit, ShowMoreRow } from '../Shared/rowLimit';

// Reusable skill-matrix table for both Yard (truck) and MRF mechanic
// development — same shape (Employee Name (ID), Position, Tenure in Role,
// Manager from Workday; N skill scores as supervisor data entry, 1-5;
// Rating/Expectation/Benchmark Rating calculated — see
// backend/src/routes/staffing.js computeMechanicRating /
// staffingConfig.yardSkillTier|mrfSkillTier; Shield Rides always
// Recruiting-owned and persisted; Promotion Date only on the Yard matrix —
// the source sheet has no such column for MRF).
//
// The 1-5 skill scale keeps its own five-step heat ramp (no token in
// theme.js covers a five-point scale) but anchors its ends to the shared
// danger/success colors so it still reads as part of the same palette.
const SKILL_COLORS = {
  1: { bg: c.dangerTint,   color: c.dangerText },
  2: { bg: '#FED7AA',      color: '#9A3412' },
  3: { bg: c.warningTint,  color: c.warningText },
  4: { bg: '#BBF7D0',      color: '#166534' },
  5: { bg: c.successTint,  color: c.successText },
};

export default function MechanicSkillTable({
  mechanics, canEdit = false, skillColumns, patchUrlBase, queryKey,
  showPromotionDate = false, showShieldRides = true,
  emptyLabel = 'No mechanics at this cost center.',
}) {
  const showPeople = mechanics.some((m) => 'worker_name' in m);
  const baseCols = 4 + skillColumns.length + 3 + (showPromotionDate ? 1 : 0) + (showShieldRides ? 1 : 0) + 1; // +Employee if shown below

  // Skill columns are each an editable 1-5 select, not something worth
  // sorting by — "Job Profile", "Supervisor" and the three calculated
  // ratings are the columns someone actually reaches for.
  const sort = useSort();
  const sorted = sortRows(mechanics, sort.key, sort.dir, {
    worker_name: (m) => m.worker_name || '',
    job_profile: (m) => m.job_profile || '',
    tenure:      (m) => m.tenure_in_role || '',
    manager:     (m) => m.manager || '',
    rating:      (m) => Number(m.rating ?? 0),
    expectation: (m) => Number(m.expectation ?? 0),
    benchmark:   (m) => Number(m.benchmark ?? 0),
    promotion:   (m) => m.promotion_date || '',
  });

  const limiter = useRowLimit(sorted);
  return (
    <ExpandableScroll>
      <table style={s.table}>
        <thead>
          <tr>
            {showPeople && <SortTh label="Employee Name (ID)" sortKey="worker_name" sort={sort} style={tbl.thSm} sticky />}
            <SortTh label="Job Profile" sortKey="job_profile" sort={sort} style={tbl.thSm} sticky />
            <SortTh label="Tenure in Role" sortKey="tenure" sort={sort} style={tbl.thSm} sticky />
            <SortTh label="Supervisor" sortKey="manager" sort={sort} style={tbl.thSm} sticky />
            {skillColumns.map((col) => (
              <SortTh key={col.key} label={col.label} align="center" style={tbl.thCSm} sticky />
            ))}
            <SortTh label="Rating" sortKey="rating" sort={sort} align="center" style={tbl.thCSm} sticky />
            <SortTh label="Expectation" sortKey="expectation" sort={sort} align="center" style={tbl.thCSm} sticky />
            <SortTh label="Benchmark Rating" sortKey="benchmark" sort={sort} align="center" style={tbl.thCSm} sticky />
            {showPromotionDate && <SortTh label="Promotion Date" sortKey="promotion" sort={sort} style={tbl.thSm} sticky />}
            {showShieldRides && <SortTh label="Shield Rides" style={tbl.thSm} sticky />}
          </tr>
        </thead>
        <tbody>
          {limiter.visible.map((m, i) => (
            <MechanicRow
              key={m.id} mechanic={m} canEdit={canEdit} showPeople={showPeople}
              skillColumns={skillColumns} patchUrlBase={patchUrlBase} queryKey={queryKey}
              showPromotionDate={showPromotionDate} showShieldRides={showShieldRides} index={i}
            />
          ))}
          <ShowMoreRow limiter={limiter} colSpan={showPeople ? baseCols : baseCols - 1} />
          {!mechanics.length && (
            <tr><td colSpan={showPeople ? baseCols : baseCols - 1} style={s.empty}>{emptyLabel}</td></tr>
          )}
        </tbody>
      </table>
    </ExpandableScroll>
  );
}

function MechanicRow({ mechanic: m, canEdit, showPeople, skillColumns, patchUrlBase, queryKey, showPromotionDate, showShieldRides, index }) {
  const qc = useQueryClient();
  const [scores, setScores] = useState(() =>
    Object.fromEntries(skillColumns.map((c) => [c.key, m[c.key] ?? '']))
  );
  const [promotionDate, setPromotionDate] = useState(m.promotion_date ? String(m.promotion_date).slice(0, 10) : '');
  const [shieldRides, setShieldRides] = useState(m.shield_rides ?? '');

  // Re-sync on prop change, not just on mount — see VacancyTable.jsx's
  // VacancyRow for why. The skill columns are read from a summary signature
  // (joined values) rather than individually, since skillColumns itself can
  // differ between the Yard and MRF instances of this component.
  const skillSignature = skillColumns.map((c) => m[c.key]).join('|');
  useEffect(() => {
    setScores(Object.fromEntries(skillColumns.map((c) => [c.key, m[c.key] ?? ''])));
    setPromotionDate(m.promotion_date ? String(m.promotion_date).slice(0, 10) : '');
    setShieldRides(m.shield_rides ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skillSignature, m.promotion_date, m.shield_rides]);

  const saveMutation = useMutation({
    mutationFn: (payload) => api.patch(`${patchUrlBase}/${m.worker_employee_id}`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [queryKey] });
      qc.invalidateQueries({ queryKey: ['staffing-costcenter'] });
    },
  });

  function currentPayload(overrides = {}) {
    const base = { ...scores };
    if (showPromotionDate) base.promotion_date = promotionDate || null;
    if (showShieldRides) base.shield_rides = shieldRides || null;
    return { ...base, ...overrides };
  }

  function saveScore(key, value) {
    const next = { ...scores, [key]: value };
    setScores(next);
    saveMutation.mutate(currentPayload({ [key]: value === '' ? null : value }));
  }
  function savePromotionDate() {
    saveMutation.mutate(currentPayload());
  }
  function saveShieldRides() {
    saveMutation.mutate(currentPayload());
  }

  const canEditThisRow = canEdit && !!m.worker_employee_id;

  return (
    <tr style={tbl.trZebra(index)}>
      {showPeople && (
        <td style={tbl.tdSm}>{m.worker_name}{m.worker_employee_id ? ` (${m.worker_employee_id})` : ''}</td>
      )}
      <td style={s.tdDesc} title={m.job_profile}>{m.job_profile || '—'}</td>
      <td style={tbl.tdSm}>{m.tenure_in_role || '—'}</td>
      <td style={tbl.tdSm}>{m.manager || '—'}</td>
      {skillColumns.map((c) => {
        const val = scores[c.key];
        const colors = val ? SKILL_COLORS[val] : null;
        return (
          <td key={c.key} style={{ ...s.tdSkill, background: colors?.bg }}>
            {canEditThisRow ? (
              <select
                style={{ ...s.skillSelect, color: colors?.color || c.textFaint, background: colors?.bg || c.surface }}
                value={val}
                onChange={(e) => saveScore(c.key, e.target.value === '' ? '' : Number(e.target.value))}
              >
                <option value="">—</option>
                {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            ) : (
              <span style={{ color: colors?.color }}>{val || '—'}</span>
            )}
          </td>
        );
      })}
      <td style={s.tdCalc}>{m.rating ?? '—'}</td>
      <td style={s.tdCalc}>{m.expectation ?? '—'}</td>
      <td style={{ ...s.tdCalc, color: m.benchmark > 0 ? c.successText : m.benchmark < 0 ? c.dangerText : c.textStrong, fontWeight: 600 }}>
        {m.benchmark ?? '—'}
      </td>
      {showPromotionDate && (
        <td style={tbl.tdSm}>
          {canEditThisRow ? (
            <input
              type="date" style={{ ...s.editInput, ...(promotionDate ? s.dateFilled : null) }}
              value={promotionDate}
              onChange={(e) => setPromotionDate(e.target.value)}
              onBlur={savePromotionDate}
            />
          ) : (m.promotion_date ? <span style={s.dateFilledText}>{fmtDate(m.promotion_date)}</span> : '—')}
        </td>
      )}
      {showShieldRides && (
        <td style={s.tdNotes}>
          {canEditThisRow ? (
            <input
              type="text" style={s.editInputWide}
              value={shieldRides}
              placeholder="e.g. 6/4/2026"
              onChange={(e) => setShieldRides(e.target.value)}
              onBlur={saveShieldRides}
            />
          ) : (m.shield_rides || '—')}
        </td>
      )}
    </tr>
  );
}

const s = {
  table:       { width: '100%', borderCollapse: 'collapse', fontSize: t.caption.fontSize },
  tdDesc:      { ...tbl.tdSm, maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  tdNotes:     { ...tbl.tdSm, minWidth: '140px' },
  tdSkill:     { padding: '4px 6px', textAlign: 'center', verticalAlign: 'middle' },
  tdCalc:      { ...tbl.tdSm, textAlign: 'center', fontWeight: 600 },
  empty:       { textAlign: 'center', padding: '24px', color: c.textFaint },
  editInput:   { padding: '4px 6px', border: `1px solid ${c.borderStrong}`, borderRadius: '4px', fontSize: t.caption.fontSize, fontFamily: 'inherit' },
  editInputWide: { width: '100%', minWidth: '110px', padding: '4px 6px', border: `1px solid ${c.borderStrong}`, borderRadius: '4px', fontSize: t.caption.fontSize, fontFamily: 'inherit' },
  skillSelect: { width: '44px', padding: '4px', border: '1px solid rgba(0,0,0,0.1)', borderRadius: '4px', fontSize: t.caption.fontSize, fontWeight: 700, textAlign: 'center', fontFamily: 'inherit' },
  dateFilled:     { color: c.successText, fontWeight: 600 },
  dateFilledText: { color: c.successText, fontWeight: 600 },
};
